<!-- components/consignee/ConsigneeDocumentUpload.vue -->
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Component } from 'vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import { notificationService } from '@/services/notificationService'
import api from '@/services/api'
import { AxiosError } from 'axios'
import {
  TrashIcon,
  FileWaveIcon,
  UploadIcon,
  FileExcleIcon,
  ImageIcon,
  FilePdfIcon,
  FileWordIcon
} from '@/icons/'

// ========== TYPE DEFINITIONS ==========
interface DocumentFile {
  id?: string
  file: File
  name: string
  size: number
  type: string
  uploadProgress?: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  errorMessage?: string
  uploadedUrl?: string
  uploadedAt?: string
}

interface Consignee {
  name: string
  designation?: string
  assignedEngineerName?: string
}

interface ExistingDocument {
  id: string
  fileName: string
  fileSize: number
  fileType: string
  fileUrl: string
  description?: string
  uploadedAt: string
  uploadedBy: string
  uploadedByName: string
}

interface ApiErrorResponse {
  message?: string
  error?: string
}

interface Props {
  visible: boolean
  tenderId: string
  consignee: Consignee | null
  existingDocuments?: ExistingDocument[]
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'documents-uploaded'): void
}

const props = withDefaults(defineProps<Props>(), {
  existingDocuments: () => []
})

const emit = defineEmits<Emits>()

// State
const documents = ref<DocumentFile[]>([])
const uploading = ref(false)
const uploadProgress = ref(0)
const existingDocs = ref<ExistingDocument[]>(props.existingDocuments)
const deletingDocId = ref<string | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)

// Computed
const canUpload = computed(() => {
  return documents.value.length > 0 && documents.value.every(doc => doc.status === 'pending')
})

const totalFilesSize = computed(() => {
  const totalBytes = documents.value.reduce((sum, doc) => sum + doc.size, 0)
  if (totalBytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(totalBytes) / Math.log(k))
  return parseFloat((totalBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
})

// Methods
const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = Array.from(target.files || [])

  // Max file size: 10MB
  const MAX_FILE_SIZE = 10 * 1024 * 1024

  // Allowed file types - Only PDF and Images
  const ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/jpg'
  ]

  // Allowed file extensions
  const ALLOWED_EXTENSIONS = /\.(pdf|jpg|jpeg|png)$/i

  let rejectedCount = 0

  for (const file of files) {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      notificationService.warning(`${file.name} exceeds 10MB limit and will be skipped`)
      rejectedCount++
      continue
    }

    // Check file type - Only PDF and Images
    const isValidType = ALLOWED_TYPES.includes(file.type)
    const isValidExtension = ALLOWED_EXTENSIONS.test(file.name)

    if (!isValidType && !isValidExtension) {
      notificationService.warning(`${file.name} is not allowed. Only PDF and image files (JPG, JPEG, PNG) are supported.`)
      rejectedCount++
      continue
    }

    // Add valid file
    documents.value.push({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'pending'
    })
  }

  // Show summary message if some files were rejected
  if (rejectedCount > 0) {
    notificationService.info(`${rejectedCount} file(s) were skipped. Only PDF and image files (JPG, JPEG, PNG) up to 10MB are allowed.`)
  }

  // Reset input value to allow selecting same file again
  target.value = ''
}

const removeDocument = (index: number) => {
  documents.value.splice(index, 1)
}

const uploadDocuments = async () => {
  // Validate required data
  if (!props.tenderId) {
    notificationService.error('Missing tender information')
    return
  }

  if (!props.consignee || !props.consignee.name) {
    notificationService.error('Missing consignee information')
    return
  }

  if (documents.value.length === 0) {
    notificationService.warning('Please select documents to upload')
    return
  }

  uploading.value = true
  uploadProgress.value = 0

  let successCount = 0
  let errorCount = 0

  for (let i = 0; i < documents.value.length; i++) {
    const doc = documents.value[i]
    if (doc.status !== 'pending') continue

    doc.status = 'uploading'

    try {
      const formData = new FormData()
      formData.append('file', doc.file)
      formData.append('tenderId', props.tenderId)
      formData.append('consigneeName', props.consignee.name)
      formData.append('fileName', doc.name)

      const response = await api.post('/api/tenders/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            doc.uploadProgress = percentCompleted

            // Update overall progress
            const totalProgress = documents.value.reduce((sum, d) => sum + (d.uploadProgress || 0), 0)
            uploadProgress.value = Math.round(totalProgress / documents.value.length)
          }
        }
      })

      if (response.data.success) {
        doc.status = 'success'
        doc.uploadedUrl = response.data.data.url
        doc.uploadedAt = response.data.data.uploadedAt
        successCount++

        // Add to existing documents list
        existingDocs.value.unshift({
          id: response.data.data.id,
          fileName: doc.name,
          fileSize: doc.size,
          fileType: doc.type,
          fileUrl: response.data.data.url,
          uploadedAt: response.data.data.uploadedAt,
          uploadedBy: response.data.data.uploadedBy,
          uploadedByName: response.data.data.uploadedByName
        })
      } else {
        throw new Error(response.data.message || 'Upload failed')
      }
    } catch (error: unknown) {
      console.error('Error uploading document:', error)
      doc.status = 'error'

      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ApiErrorResponse
        doc.errorMessage = errorData?.message || error.message || 'Upload failed'
      } else if (error instanceof Error) {
        doc.errorMessage = error.message
      } else {
        doc.errorMessage = 'Upload failed'
      }
      errorCount++
    }
  }

  uploading.value = false

  if (successCount > 0) {
    notificationService.success(`Successfully uploaded ${successCount} document(s)`)
    // Clear uploaded documents from pending list
    documents.value = documents.value.filter(doc => doc.status !== 'success')
    emit('documents-uploaded')
  }

  if (errorCount > 0) {
    notificationService.error(`Failed to upload ${errorCount} document(s)`)
  }
}

const deleteDocument = async (docId: string) => {
  if (!props.consignee?.name) {
    notificationService.error('Missing consignee information')
    return
  }

  if (!confirm('Are you sure you want to delete this document?')) return

  deletingDocId.value = docId

  try {
    const response = await api.delete(`/api/tenders/documents/${docId}`, {
      data: {
        tenderId: props.tenderId,
        consigneeName: props.consignee.name
      }
    })

    if (response.data.success) {
      existingDocs.value = existingDocs.value.filter(doc => doc.id !== docId)
      notificationService.success('Document deleted successfully')
    } else {
      throw new Error(response.data.message || 'Delete failed')
    }
  } catch (error: unknown) {
    console.error('Error deleting document:', error)

    if (error instanceof AxiosError) {
      const errorData = error.response?.data as ApiErrorResponse
      notificationService.error(errorData?.message || 'Failed to delete document')
    } else if (error instanceof Error) {
      notificationService.error(error.message)
    } else {
      notificationService.error('Failed to delete document')
    }
  } finally {
    deletingDocId.value = null
  }
}

const downloadDocument = (doc: ExistingDocument) => {
  window.open(doc.fileUrl, '_blank')
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const getFileIcon = (fileType: string): Component => {
  const type = fileType.toLowerCase()

  // Check for specific file types
  if (type.includes('pdf')) {
    return FilePdfIcon
  }

  // Word documents
  if (type.includes('word') ||
      type.includes('doc') ||
      type.includes('msword') ||
      type.includes('document')) {
    return FileWordIcon
  }

  // Excel/Spreadsheet documents
  if (type.includes('excel') ||
      type.includes('sheet') ||
      type.includes('xls') ||
      type.includes('spreadsheet') ||
      type.includes('ms-excel')) {
    return FileExcleIcon
  }

  // Image files
  if (type.includes('jpg') ||
      type.includes('jpeg') ||
      type.includes('png') ||
      type.includes('gif') ||
      type.includes('bmp') ||
      type.includes('image')) {
    return ImageIcon
  }

  // Default icon for other file types (txt, zip, etc.)
  return FileWaveIcon
}

const closeModal = () => {
  emit('update:visible', false)
}

watch(() => props.visible, (newVal) => {
  if (!newVal) {
    // Reset state when modal closes
    documents.value = []
    uploading.value = false
    uploadProgress.value = 0
  }
})

watch(() => props.existingDocuments, (newDocs) => {
  existingDocs.value = newDocs || []
}, { deep: true })
</script>

<template>
  <ModalDefault
    :modelValue="visible"
    @update:modelValue="closeModal"
    title="Upload Documents"
    width="max-w-3xl"
    :closeOnOutsideClick="!uploading"
  >
    <div class="space-y-6">
      <!-- Consignee Info -->
      <div v-if="consignee" class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h4 class="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Uploading for:</h4>
        <div class="space-y-1">
          <p class="text-gray-900 dark:text-gray-100 font-medium">{{ consignee.name || 'Unnamed Consignee' }}</p>
          <p v-if="consignee.designation" class="text-sm text-gray-600 dark:text-gray-400">
            {{ consignee.designation }}
          </p>
          <p v-if="consignee.assignedEngineerName" class="text-sm text-gray-600 dark:text-gray-400">
            Assigned Engineer: {{ consignee.assignedEngineerName }}
          </p>
        </div>
      </div>

      <!-- Existing Documents Section -->
      <div v-if="existingDocs.length > 0" class="space-y-3">
        <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Existing Documents</h4>
        <div class="space-y-2 max-h-64 overflow-y-auto">
          <div
            v-for="doc in existingDocs"
            :key="doc.id"
            class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-sm transition-shadow"
          >
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <component
                :is="getFileIcon(doc.fileType)"
                class="w-8 h-8 mt-1"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {{ doc.fileName }}
                </p>
                <div class="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>{{ formatFileSize(doc.fileSize) }}</span>
                  <span>•</span>
                  <span>Uploaded: {{ formatDate(doc.uploadedAt) }}</span>
                  <span v-if="doc.uploadedByName">•</span>
                  <span v-if="doc.uploadedByName">By: {{ doc.uploadedByName }}</span>
                </div>
                <p v-if="doc.description" class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {{ doc.description }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <BaseButton
                @click="downloadDocument(doc)"
                text="Download"
                size="xs"
                variant="secondary"
              />
              <BaseButton
                @click="deleteDocument(doc.id)"
                :icon="TrashIcon"
                size="xs"
                variant="danger"
                :loading="deletingDocId === doc.id"
                :disabled="uploading"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Upload Section -->
      <div class="space-y-4">
        <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Upload New Documents</h4>

        <!-- File Upload Area -->
        <div
          class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
          @click="fileInputRef?.click()"
        >
          <UploadIcon class="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Click to select files or drag and drop
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
            <span class="font-semibold">Supported formats:</span> PDF, JPG, JPEG, PNG (Max 10MB each)
          </p>
          <input
            ref="fileInputRef"
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            class="hidden"
            @change="handleFileSelect"
            :disabled="uploading"
          />
        </div>

        <!-- Selected Documents List -->
        <div v-if="documents.length > 0" class="space-y-3">
          <div class="flex justify-between items-center">
            <span class="text-xs font-medium text-gray-600 dark:text-gray-400">
              Selected Files ({{ documents.length }} files, {{ totalFilesSize }})
            </span>
          </div>

          <div class="space-y-2 max-h-80 overflow-y-auto">
            <div
              v-for="(doc, index) in documents"
              :key="index"
              class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div class="flex items-start gap-3">
                <component
                  :is="getFileIcon(doc.type)"
                  class="w-8 h-8 mt-1"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {{ doc.name }}
                      </p>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {{ formatFileSize(doc.size) }}
                      </p>
                    </div>
                    <BaseButton
                      v-if="doc.status !== 'uploading' && doc.status !== 'success'"
                      @click="removeDocument(index)"
                      :icon="TrashIcon"
                      size="xs"
                      variant="danger"
                    />
                  </div>

                  <!-- Upload Progress -->
                  <div v-if="doc.status === 'uploading'" class="mt-2">
                    <div class="w-full bg-gray-200 rounded-full h-2">
                      <div
                        class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        :style="{ width: `${doc.uploadProgress || 0}%` }"
                      ></div>
                    </div>
                    <p class="text-xs text-gray-500 mt-1">{{ doc.uploadProgress || 0 }}% uploaded</p>
                  </div>

                  <!-- Success Status -->
                  <div v-if="doc.status === 'success'" class="mt-2">
                    <span class="text-xs text-green-600 dark:text-green-400">✓ Uploaded successfully</span>
                  </div>

                  <!-- Error Status -->
                  <div v-if="doc.status === 'error'" class="mt-2">
                    <span class="text-xs text-red-600 dark:text-red-400">Error: {{ doc.errorMessage }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton
          text="Cancel"
          variant="secondary"
          @click="closeModal"
          :disabled="uploading"
        />
        <BaseButton
          text="Upload"
          variant="primary"
          :loading="uploading"
          :disabled="!canUpload"
          @click="uploadDocuments"
        />
      </div>
    </template>
  </ModalDefault>
</template>
